import { type FacetTerm } from '@commercetools/platform-sdk'
import { type ClerkFacet } from '#app/constants/clerk-api'
import { substituteSymbol } from '#app/utils/string-utils'

const sizeOrder = Object.freeze([
	'XXXS',
	'XXS',
	'2XS',
	'XS',
	'XS/S',
	'S',
	'S/M',
	'M',
	'M/L',
	'L',
	'L/XL',
	'XL',
	'XL/XXL',
	'2XL',
	'XXL',
	'XXXL',
])

export const sortFacets = (a: FacetTerm, b: FacetTerm): any => {
	const isNumericalA = /^\d+$/.test(a.term)
	const isNumericalB = /^\d+$/.test(b.term)
	if (isNumericalA && isNumericalB) {
		return parseInt(a.term) - parseInt(b.term)
	} else {
		// Custom comparison for alphabetical sizes
		const indexA = sizeOrder.indexOf(a.term)
		const indexB = sizeOrder.indexOf(b.term)

		// If any size is not found in the custom order, resort to alphabetical comparison
		if (indexA !== -1 && indexB !== -1) {
			if (indexA !== indexB) {
				return indexA - indexB
			}
		} else {
			// If either size is not found in the custom order, resort to alphabetical comparison
			return a.term.localeCompare(b.term)
		}
	}
}

export const sortClerkFacets = (a: any, b: any): any => {
	const isNumericalA = /^\d+$/.test(a.value)
	const isNumericalB = /^\d+$/.test(b.value)
	if (isNumericalA && isNumericalB) {
		return parseInt(a.value) - parseInt(b.value)
	} else {
		// Custom comparison for alphabetical sizes
		const indexA = sizeOrder.indexOf(a.value)
		const indexB = sizeOrder.indexOf(b.value)

		// If any size is not found in the custom order, resort to alphabetical comparison
		if (indexA !== -1 && indexB !== -1) {
			if (indexA !== indexB) {
				return indexA - indexB
			}
		} else {
			// If either size is not found in the custom order, resort to alphabetical comparison
			return a.value.localeCompare(b.value)
		}
	}
}

export const sortClerkFacetBySize = (a: ClerkFacet, b: ClerkFacet) => {
	const sizeA = a.value
	const sizeB = b.value

	// Check if sizes are numerical
	const isNumericalA = /^\d+$/.test(sizeA)
	const isNumericalB = /^\d+$/.test(sizeB)

	// Check if sizes are in the format "number-number"
	const isRangeFormatA = /^\d+-\d+$/.test(sizeA) || /^\d+\/\d+$/.test(sizeA)
	const isRangeFormatB = /^\d+-\d+$/.test(sizeB) || /^\d+\/\d+$/.test(sizeB)

	if ((isRangeFormatA && isNumericalB) || (isNumericalA && isRangeFormatB)) {
		// If one size is in the format "number-number" or "number/number" and the other size is a single number
		const firstPartA = parseInt(sizeA.split('-')[0].split('/')[0])
		const firstPartB = parseInt(sizeB.split('-')[0].split('/')[0])

		return firstPartA - firstPartB
	} else if (isRangeFormatA && isRangeFormatB) {
		// If both sizes are in the format "number-number" or "number/number"
		const [firstNumberA, secondNumberA] = sizeA
			.replace('/', '-')
			.split('-')
			.map(Number)
		const [firstNumberB, secondNumberB] = sizeB
			.replace('/', '-')
			.split('-')
			.map(Number)

		// Sort first by the first number, then by the second number
		if (firstNumberA === firstNumberB) {
			return secondNumberA - secondNumberB
		}
		return firstNumberA - firstNumberB
	} else if (isNumericalA && isNumericalB) {
		// If sizes are numerical

		// If sizes are different, sort by size
		return parseInt(sizeA) - parseInt(sizeB)
	} else {
		// Custom comparison for alphabetical sizes
		const indexA = sizeOrder.indexOf(sizeA)
		const indexB = sizeOrder.indexOf(sizeB)

		// If any size is not found in the custom order, resort to alphabetical comparison
		if (indexA !== -1 && indexB !== -1) {
			if (indexA !== indexB) {
				return indexA - indexB
			}
		} else {
			// If either size is not found in the custom order, resort to alphabetical comparison
			return sizeA.localeCompare(sizeB)
		}
	}
	// Default case, no special conditions met
	return 0
}

// Only takes dashes and slashes into account
// Returns the first number seperated by the delimiter
export function getSpecificSize(size: string): string {
	let splitString = size
	if (containsDashOrSlash(splitString)) {
		splitString = splitString.split('/', 1)[0]
		splitString = splitString.split('-', 1)[0]
	}
	return splitString
}

// Could be made into a full regex with special characters
export function containsDashOrSlash(string: string): boolean {
	if (string.includes('-')) return true
	if (string.includes('/')) return true
	return false
}

// Sort a list of size facets smallest to largest
export function sortSizes(facets: FacetTerm[]): FacetTerm[] {
	let sortedFacets: FacetTerm[] = []
	sortedFacets = facets.sort((facet1, facet2) => {
		return facet1.term < facet2.term ? -1 : facet1.term < facet2.term ? 1 : 0
	})
	return sortedFacets
}

// Sort a list of size facets smallest to largest
export function sortClerkSizes(facets: ClerkFacet[]): ClerkFacet[] {
	let sortedFacets: ClerkFacet[] = []
	sortedFacets = facets.sort((facet1, facet2) => {
		return facet1.value < facet2.value
			? -1
			: facet1.value < facet2.value
			  ? 1
			  : 0
	})
	return sortedFacets
}

// Remove duplicates in a sorted list
export function removeDuplicatesInSortedFacetList(
	facets: FacetTerm[],
): FacetTerm[] {
	let originFacets = facets
	let singletonFacets: FacetTerm[] = []
	originFacets.forEach((facet, index) => {
		let previousFacet = index > 0 ? originFacets[index - 1] : null
		let currentFacet = facet
		let nextFacet =
			index < originFacets.length - 1 ? originFacets[index + 1] : null

		if (index == 0) {
			if (nextFacet != null) {
				if (areFacetTermsMatching(currentFacet.term, nextFacet.term)) {
					singletonFacets.push(updateFacet(currentFacet, nextFacet))
				} else {
					singletonFacets.push(currentFacet)
				}
			} else {
				singletonFacets.push(currentFacet)
			}
		}
		if (index > 0) {
			if (previousFacet != null) {
				if (!areFacetTermsMatching(currentFacet.term, previousFacet.term)) {
					if (nextFacet != null) {
						if (areFacetTermsMatching(currentFacet.term, nextFacet.term)) {
							singletonFacets.push(updateFacet(currentFacet, nextFacet))
						} else {
							singletonFacets.push(currentFacet)
						}
					}
				}
			}
		}
	})
	return singletonFacets
}

export function updateFacet(facet1: FacetTerm, facet2: FacetTerm): FacetTerm {
	let newFacet: FacetTerm = {
		term: substituteSymbol(facet1.term, '-', '/'),
		count: facet1.count + facet2.count,
		productCount: facet1.productCount! + facet2.productCount!,
	}
	return newFacet
}

export function updateClerkFacet(
	facet1: ClerkFacet,
	facet2: ClerkFacet,
): ClerkFacet {
	let newFacet: ClerkFacet = {
		value: substituteSymbol(facet1.value, '-', '/'),
		name: facet1.name,
		type: 'unit',
		count: facet1.count + facet2.count,
	}
	return newFacet
}

// Check whether the 2 facet terms are equal
export function areFacetTermsMatching(
	facetTerm1: string,
	facetTerm2: string,
): boolean {
	return substituteSymbol(facetTerm1, '-', '/') ==
		substituteSymbol(facetTerm2, '-', '/')
		? true
		: false
}
